import { createListenerMiddleware, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { dataWranglerApiSlice } from "../../api/dataWranglerApiSlice";
import { selectParticipantLimit } from "./dataWranglerSlice";
import { transfer } from "@globus/sdk";

export type UserCollection = {
    id: string;
    label: string;
    directory?: string;
}

const initialState = {
    userCollection: {
        id: null,
        label: null,
        directory: "/~/",
    },
    preparingGlobusTransfer: {
        status: false,
        complete: false,
        page: 0,
        pageCount: 0,
        error: false,
        errorMessage: ''
    }
}

const globusSlice = createSlice({
    name: 'globus',
    initialState: initialState as any,
    reducers: {
        setUserCollection: (state, action: PayloadAction<UserCollection>) => {
            state.userCollection.id = action.payload.id;
            state.userCollection.label = action.payload.label;
            state.userCollection.directory = action.payload.directory || initialState.userCollection.directory;
        },
        resetUserCollection: (state) => {
            state.userCollection = initialState.userCollection;
        },
        setUserCollectionDirectory: (state, action: PayloadAction<string>) => {
            state.userCollection.directory = action.payload;
        },
        globusTransferFiles: (state, action: PayloadAction<any>) => {
            state.preparingGlobusTransfer = {
                status: true,
                complete: false,
                page: action.payload.page,
                pageCount: action.payload.pageCount,
                error: false,
                errorMessage: ''
            }
        },
        globusTransferFilesComplete: (state) => {
            state.preparingGlobusTransfer = {
                status: true,
                complete: true,
                page: state.preparingGlobusTransfer.page + 1,
                pageCount: state.preparingGlobusTransfer.pageCount,
                error: false,
                errorMessage: ''
            }
        },
        globusTransferFilesError: (state, action: PayloadAction<string>) => {
            state.preparingGlobusTransfer = {
                status: false,
                complete: false,
                page: 0,
                pageCount: 0,
                error: true,
                errorMessage: action.payload
            }
        },
        globusTransferFilesReset: (state) => {
            state.preparingGlobusTransfer = initialState.preparingGlobusTransfer;
        },
    }
});

export const {
    setUserCollection,
    resetUserCollection,
    setUserCollectionDirectory,
    globusTransferFiles,
    globusTransferFilesComplete,
    globusTransferFilesError,
    globusTransferFilesReset,
} = globusSlice.actions;

export default globusSlice.reducer;

export const selectGlobusTransferStatus = (state: any) => state.globus.preparingGlobusTransfer;
export const selectUserCollection = (state: any) => state.globus.userCollection;

const globusListenerMiddleware = createListenerMiddleware();

globusListenerMiddleware.startListening({
    actionCreator: globusTransferFiles,
    effect: async (action, listenerApi) => {
        const participantLimit = selectParticipantLimit(listenerApi.getState());
        const globusFileSummary = localStorage.getItem("globus-file-summary");
        const { data: files } = await listenerApi.dispatch(dataWranglerApiSlice.endpoints
            .adwTransferFiles.initiate({
                summary: globusFileSummary,
                selected: {
                    query: action.payload.fileSelection,
                    participants: action.payload.participantFilter
                },
                type: "",
                page: action.payload.page,
                limit: window.env.globusLimit,
                participantLimit: Number.isInteger(participantLimit) ? participantLimit : null,
            }));
        const submissionResponse = await transfer.taskSubmission.submissionId(
            {}, { manager: action.payload.authorization })
        const deadline = new Date();
        deadline.setDate(deadline.getDate() + 30)
        deadline.setHours(deadline.getHours() - 1)
        const submission = await submissionResponse.json();
        const transferPayload: any = {
            submission_id: submission.value,
            source_endpoint: action.payload.source,
            destination_endpoint: action.payload.collection,
            deadline: deadline.toISOString(),
            label: `Lasso Informatics ${action.payload.study} File Transfer (${action.payload.page + 1} of ${action.payload.pageCount})`,
            sync_level: 1,
            DATA: []
        }

        files?.forEach((file: any) => {
            transferPayload['DATA'].push({
                DATA_TYPE:"transfer_item",
                source_path: `/${file['filename']}`,
                destination_path: `${action.payload.path}/${file['filename']}`
            });
        });

        const transferPromise = await transfer.taskSubmission.submitTransfer({
            payload: transferPayload
        }, { manager: action.payload.authorization });
        const transferResponse = await transferPromise.json();

        if (['Accepted', 'Duplicate'].includes(transferResponse.code)) {
            if (files.length >= window.env.globusLimit) {
                listenerApi.dispatch(globusTransferFiles({
                    ...action.payload,
                    page: action.payload.page + 1
                }))
            } else {
                listenerApi.dispatch(globusTransferFilesComplete())
            }
        } else {
            listenerApi.dispatch(globusTransferFilesError(
                transferResponse.message ?? 'There was an error while processing your request'))
        }
    }
})

export { globusListenerMiddleware };